import React from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";

const COLS = [
  ["due_this_week", "Due this week", "CalendarClock", "text-[#4A78A8]"],
  ["overdue", "Overdue", "AlarmClock", "text-[#C95555]"],
  ["blockers", "Blocked", "OctagonAlert", "text-[#C95555]"],
  ["completed_last_7_days", "Completed (7 days)", "CircleCheck", "text-[#3D8B65]"],
];

function List({ items, render }) {
  if (!items.length) return <p className="text-xs text-[#1E2D44]/35">Nothing</p>;
  return (
    <ul className="space-y-1.5">
      {items.slice(0, 4).map((x, i) => <li key={x.id || x.kpi_id + i} className="text-xs text-[#1E2D44]/80">{render(x)}</li>)}
      {items.length > 4 && <li className="text-[11px] text-[#1E2D44]/45">+{items.length - 4} more</li>}
    </ul>
  );
}

const workLine = (a) => (
  <>
    <span className="block truncate font-medium text-[#0B1729]">{a.title}</span>
    {a.blocker ? <span className="block truncate text-[#C95555]">{a.blocker}</span> : a.link_label && <span className="block truncate text-[#1E2D44]/45">{a.link_label}</span>}
  </>
);

// Pre-filled from linked work and KPIs so the weekly check-in never needs re-typing.
export function DeliveryPanel({ d }) {
  const navigate = useNavigate();
  return (
    <div className="amden-card mb-6 p-6" data-testid="delivery-panel">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-bold text-[#0B1729]">Your delivery this week</h3>
          <p className="text-xs text-[#1E2D44]/50">Pulled from your linked work and KPIs — it's already filled into your check-in answers.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate("/workspace")} data-testid="delivery-open-workspace">
          <Icons.Briefcase size={14} className="mr-1.5" /> Open My Workspace
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {COLS.map(([k, label, icon, tone]) => (
          <div key={k} data-testid={`delivery-${k}`}>
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-[#1E2D44]/65">
              {React.createElement(Icons[icon], { size: 13, className: tone })} {label}
              <span className="rounded-full bg-[#E4E8EF] px-1.5 text-[10px]">{d[k].length}</span>
            </div>
            <List items={d[k]} render={workLine} />
          </div>
        ))}
        <div data-testid="delivery-kpi_updates_due">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-[#1E2D44]/65">
            <Icons.Gauge size={13} className="text-[#B87F1E]" /> KPI updates due
            <span className="rounded-full bg-[#E4E8EF] px-1.5 text-[10px]">{d.kpi_updates_due.length}</span>
          </div>
          <List items={d.kpi_updates_due} render={(k) => <><span className="block truncate font-medium text-[#0B1729]">{k.name}</span><span className="block truncate text-[#1E2D44]/45">{k.period} · {k.state === "missing" ? "not reported" : "due"}</span></>} />
        </div>
      </div>
    </div>
  );
}

const bullets = (xs, f = (a) => a.title) => xs.map((x) => `• ${f(x)}`).join("\n");
const compact = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v));

export function deliveryPrefill(d) {
  if (!d) return { monday: {}, friday: {} };
  return {
    monday: compact({
      by_friday: bullets(d.due_this_week),
      blocked: bullets(d.blockers, (a) => `${a.title}${a.blocker ? ` — ${a.blocker}` : ""}`),
    }),
    friday: compact({
      completed: bullets(d.completed_last_7_days),
      not_completed: bullets([...d.overdue, ...d.due_this_week]),
    }),
  };
}
